<template>
  <div>
    <a-modal class="create-user-modal" v-model="visible" title="Basic Modal">
      <div class="title mb-40px">{{ $t('btn.set_user_as_admin') }}</div>
      <form-select
          class="mb-32px"
          :label="$t('label.organization')"
          :placeholder="$t('label.organization')"
          :value="form.organization_id"
          :options="organizationsList"
          @change="form.organization_id = $event"
      />
      <div class="email-search-row mb-32px">
        <form-input
            class="email-input"
            :value="form.email"
            :label="$t('label.email')"
            :placeholder="$t('label.email')"
            :disabled="!form.organization_id"
            :error="error.user_email"
            @input="form.email = $event"
        />
        <a-button
            class="app-button primary search-btn"
            :disabled="!form.organization_id || !form.email"
            :loading="searching"
            @click="searchUser"
        >
          {{ $t('label.search') }}
        </a-button>
      </div>
      <div v-if="user" class="user-info mb-32px">
        <div class="user-info-row">
          <span class="user-info-label">{{ $t('label.first_name') }}:</span>
          <span>{{ user.first_name }}</span>
        </div>
        <div class="user-info-row">
          <span class="user-info-label">{{ $t('label.last_name') }}:</span>
          <span>{{ user.last_name }}</span>
        </div>
        <div v-if="user.organizations && user.organizations.length" class="user-info-row">
          <span class="user-info-label">{{ $t('label.list_of_organizations') }}:</span>
          <ul class="user-orgs-list">
            <li v-for="org in user.organizations" :key="org.id">{{ org.name }}</li>
          </ul>
        </div>
      </div>
      <form-checkbox
          v-if="user"
          class="mb-40px"
          :label="userName"
          @change="form.confirm = $event"
      />
      <template slot="footer">
        <a-button key="back" class="app-button" @click="$emit('close')">
          {{ $t('btn.cancel') }}
        </a-button>
        <a-button key="submit" class="app-button primary" :loading="loading" :disabled="!form.confirm" @click="submit">
          {{ $t('btn.set_user_as_admin') }}
        </a-button>
      </template>
    </a-modal>
  </div>
</template>

<script>
import validationMixin from '@/mixins/validation';
import FormCheckbox from '@/components/elements/FormCheckbox';
import FormInput from '@/components/elements/FormInput';
import FormSelect from '@/components/elements/FormSelect';

export default {
  components: {
    FormCheckbox,
    FormInput,
    FormSelect
  },
  mixins: [validationMixin],
  props: {
    show: {type: Boolean, default: false},
    organizationsList: {type: Array, default: () => []}
  },
  data: () => ({
    loading: false,
    searching: false,
    visible: false,
    user: null,
    form: {
      confirm: false,
      email: null,
      organization_id: null
    },
    error: {
      user_email: null
    }
  }),
  computed: {
    userName() {
      if (!this.user) return '';
      return `${this.user.first_name} ${this.user.last_name}`.trim() || this.user.email;
    }
  },
  watch: {
    show(value) {
      this.visible = value;
    },
    visible(value) {
      if (!value) {
        this.clearError();
        this.clearForm();
        this.$emit('close');
      }
    }
  },
  methods: {
    clearError() {
      Object.entries(this.error).map(el => this.error[el[0]] = null);
    },
    clearForm() {
      this.user = null;
      Object.entries(this.form).map(el => this.form[el[0]] = null);
    },
    searchUser() {
      if (!this.validateEmail(this.form.email)) {
        this.error.user_email = this.$t('error.user_not_found');
        return;
      }

      this.error.user_email = null;
      this.user = null;
      this.form.confirm = false;
      this.searching = true;

      this.$store.dispatch('usersStore/SEARCH_USER_BY_EMAIL', this.form.email)
        .then(response => {
          this.user = response.data;
        })
        .catch(error => {
          if (error.response && error.response.status === 404) {
            this.error.user_email = this.$t('error.user_not_found');
          } else if (error.response && error.response.status === 422) {
            for (const [key, errorMessage] of Object.entries(error.response.data.error.errors)) {
              this.error[key] = errorMessage[0];
            }
          } else {
            this.$message.error(
              (error.response && error.response.data && error.response.data.error && error.response.data.error.message)
              || this.$t('alert.something_goes_wrong')
            );
          }
        })
        .finally(() => {
          this.searching = false;
        });
    },
    submit() {
      this.$store.dispatch('usersStore/SET_USER_AS_ADMIN', {
        organization_id: this.form.organization_id,
        user_ids: [this.user.id]
      })
      .then(() => {
        this.visible = false;
        this.$message.success(this.$t('alert.admin_created'));
      })
      .catch(error => {
        if (error.response.status === 422) {
          for (const [key, errorMessage] of Object.entries(error.response.data.error.errors)) {
            this.error[key] = errorMessage[0];
          }
        } else {
          this.$message.error(error.response.data.error.message || this.$t('alert.something_goes_wrong'));
        }
      })
    },
  },
};
</script>

<style lang="scss" scoped>
.create-user-modal::v-deep {
  .ant-modal {
    width: 590px !important;

    .title {
      color: #000;
      font-size: 36px;
      font-weight: 500;
      text-align: center;
    }
  }
}

.email-search-row {
  display: flex;
  align-items: flex-end;
  gap: 12px;

  .email-input {
    flex: 1;
  }

  .search-btn {
    margin-bottom: 0;
    height: 40px;
  }
}

.user-info {
  background: #f5f5f5;
  border-radius: 8px;
  padding: 16px;

  .user-info-row {
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .user-info-label {
    font-weight: 600;
    margin-right: 8px;
  }

  .user-orgs-list {
    margin: 4px 0 0 16px;
    padding: 0;
    list-style: disc;
  }
}
</style>
